import type { Column, ColumnFiltersState } from '@tanstack/react-table'
import { Check, Filter, X } from 'lucide-react'
import { memo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTranslation } from '@/hooks/use-translation'

import { FilterFactory } from './components/filters/FilterFactory'
import {
  dataTableControlBadgeClassName,
  dataTableControlClassName,
  dataTableControlIconClassName,
} from './data-table-control-styles'
import { useFilterConfig } from './hooks/use-filter-config'
import type { DataTableFiltersProps, FilterConfig, FilterOption, FilterValue } from './types/data-table.types'

const EMPTY_FILTERS: ColumnFiltersState = []

interface FilterInputProps<TData> {
  column: Column<TData, unknown>
  value: FilterValue
  onChange: (columnId: string, value: unknown) => void
  getFilterConfig: (column: Column<TData, unknown>) => FilterConfig
  getFilterType: (column: Column<TData, unknown>) => string
  getFilterTitle: (column: Column<TData, unknown>) => string
  getFilterOptions: (column: Column<TData, unknown>) => FilterOption[]
  hasCustomFilter: (column: Column<TData, unknown>) => boolean
}

function FilterInput<TData>({
  column,
  value,
  onChange,
  getFilterConfig,
  getFilterType,
  getFilterTitle,
  getFilterOptions,
  hasCustomFilter,
}: FilterInputProps<TData>) {
  return (
    <FilterFactory
      column={column}
      value={value}
      onChange={(v) => onChange(column.id, v)}
      getFilterConfig={getFilterConfig}
      getFilterType={getFilterType}
      getFilterTitle={getFilterTitle}
      getFilterOptions={getFilterOptions}
      hasCustomFilter={hasCustomFilter}
    />
  )
}

function DataTableFiltersComponent<TData>({
  columns,
  excludeSearchKey,
  filters = EMPTY_FILTERS,
  onFiltersChange,
}: DataTableFiltersProps<TData>) {
  const t = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState<ColumnFiltersState>(EMPTY_FILTERS)
  const prevFiltersRef = useRef<ColumnFiltersState>(EMPTY_FILTERS)

  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters
    setLocalFilters(filters)
  }

  const {
    filterableColumns,
    getFilterConfig,
    getFilterType,
    getFilterTitle,
    getFilterOptions,
    hasCustomFilter,
  } = useFilterConfig(columns)

  // Filter out excluded columns
  const availableColumns = filterableColumns.filter(
    (column) => column.id !== 'actions' && column.id !== excludeSearchKey,
  )

  const activeFiltersCount = filters.filter((filter) => filter.id !== excludeSearchKey).length
  const localActiveFiltersCount = localFilters.filter(
    (filter) => filter.id !== excludeSearchKey,
  ).length

  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters)

  const handleClearAllFilters = () => {
    setLocalFilters([])
    onFiltersChange?.([])
    setIsOpen(false)
  }

  const handleClearFilter = (columnId: string) => {
    const newFilters = localFilters.filter((filter) => filter.id !== columnId)
    setLocalFilters(newFilters)
  }

  const handleFilterChange = (columnId: string, value: unknown) => {
    const existingFilterIndex = localFilters.findIndex((filter) => filter.id === columnId)
    let newFilters: ColumnFiltersState

    if (value === undefined || value === '') {
      // Remove filter if value is empty
      newFilters = localFilters.filter((filter) => filter.id !== columnId)
    } else if (existingFilterIndex >= 0) {
      // Update existing filter
      newFilters = [...localFilters]
      newFilters[existingFilterIndex] = { id: columnId, value }
    } else {
      // Add new filter
      newFilters = [...localFilters, { id: columnId, value }]
    }

    setLocalFilters(newFilters)
  }

  const handleApplyFilters = () => {
    onFiltersChange?.(localFilters)
    setIsOpen(false)
  }

  const getFilterValue = (columnId: string): FilterValue => {
    const filter = localFilters.find((f) => f.id === columnId)
    return filter?.value as FilterValue
  }

  return (
    <div className="flex items-center space-x-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={dataTableControlClassName}>
            <Filter className={dataTableControlIconClassName} />
            {t('dataTable.filters')}
            {activeFiltersCount > 0 && (
              <span className={dataTableControlBadgeClassName}>
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-0 flex flex-col max-h-[min(80vh,32rem)]"
          align="start"
        >
          <div className="flex items-center justify-between min-h-[24px] shrink-0 px-3 pt-3 pb-2">
            <h4 className="font-medium text-sm">{t('dataTable.filters')}</h4>
            <div className="min-h-[24px] flex items-center">
              {localActiveFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllFilters}
                  className="h-6 px-2 text-xs"
                >
                  <X className="mr-1 h-3 w-3" />
                  {t('dataTable.clearAll')}
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-1 space-y-3 min-h-0">
            {availableColumns.map((column) => (
              <div key={column.id} className="space-y-1">
                <FilterInput
                  column={column}
                  value={getFilterValue(column.id)}
                  onChange={handleFilterChange}
                  getFilterConfig={getFilterConfig}
                  getFilterType={getFilterType}
                  getFilterTitle={getFilterTitle}
                  getFilterOptions={getFilterOptions}
                  hasCustomFilter={hasCustomFilter}
                />
                {!!getFilterValue(column.id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleClearFilter(column.id)}
                    className="h-5 px-1 text-xs"
                  >
                    <X className="mr-1 h-3 w-3" />
                    {t('dataTable.clear')}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-2 px-3 py-2 border-t shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setLocalFilters(filters)
                setIsOpen(false)
              }}
              className="h-7 text-xs"
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleApplyFilters}
              disabled={!hasChanges}
              className="h-7 text-xs"
            >
              <Check className="mr-1 h-3 w-3" />
              {t('dataTable.applyFilters')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export const DataTableFilters = memo(DataTableFiltersComponent) as typeof DataTableFiltersComponent
